import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #0a0e1a;
    --surface: #111827;
    --surface2: #1a2235;
    --border: rgba(255,255,255,0.07);
    --accent: #00d4ff;
    --accent2: #7b5ea7;
    --success: #22c55e;
    --text: #e8edf5;
    --muted: #6b7a99;
    --font-display: 'Syne', sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
  }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-display);
    min-height: 100vh;
    overflow-x: hidden;
  }

  .landing {
    min-height: 100vh;
    background: radial-gradient(ellipse at 20% 50%, rgba(0,212,255,0.04) 0%, transparent 60%),
                radial-gradient(ellipse at 80% 20%, rgba(123,94,167,0.06) 0%, transparent 50%),
                var(--bg);
  }

  /* NAV */
  .nav {
    padding: 20px 48px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(10,14,26,0.85);
    backdrop-filter: blur(12px);
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .nav-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 20px;
    font-weight: 800;
    color: var(--text);
    letter-spacing: 0.02em;
    cursor: pointer;
  }

  .nav-logo span { font-size: 26px; }

  .nav-actions { display: flex; align-items: center; gap: 12px; }

  .btn-ghost {
    background: transparent;
    color: var(--muted);
    border: 1px solid var(--border);
    padding: 9px 20px;
    font-size: 14px;
    font-weight: 600;
    font-family: var(--font-display);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-ghost:hover { color: var(--text); border-color: rgba(255,255,255,0.2); }

  .btn-accent {
    background: var(--accent);
    color: #0a0e1a;
    border: none;
    padding: 9px 20px;
    font-size: 14px;
    font-weight: 700;
    font-family: var(--font-display);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-accent:hover { background: #33dcff; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(0,212,255,0.25); }

  /* HERO */
  .hero {
    max-width: 860px;
    margin: 0 auto;
    padding: 100px 24px 80px;
    text-align: center;
  }

  .hero-eyebrow {
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.2em;
    color: var(--accent);
    text-transform: uppercase;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .hero-eyebrow::before, .hero-eyebrow::after {
    content: '';
    width: 32px;
    height: 1px;
    background: var(--accent);
    opacity: 0.4;
  }

  .hero-title {
    font-size: clamp(42px, 7vw, 72px);
    font-weight: 800;
    line-height: 1.05;
    margin-bottom: 24px;
    background: linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.6) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .hero-title .accent {
    background: linear-gradient(135deg, var(--accent), var(--accent2));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .hero-sub {
    font-size: 18px;
    color: var(--muted);
    line-height: 1.7;
    margin-bottom: 48px;
    max-width: 560px;
    margin-left: auto;
    margin-right: auto;
  }

  .hero-actions {
    display: flex;
    gap: 14px;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 64px;
  }

  .btn-primary-lg {
    background: linear-gradient(135deg, var(--accent), var(--accent2));
    color: white;
    border: none;
    padding: 16px 40px;
    font-size: 16px;
    font-weight: 700;
    font-family: var(--font-display);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;
    letter-spacing: 0.02em;
  }

  .btn-primary-lg:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(0,212,255,0.3); }

  .btn-secondary-lg {
    background: var(--surface);
    color: var(--text);
    border: 1px solid var(--border);
    padding: 16px 40px;
    font-size: 16px;
    font-weight: 600;
    font-family: var(--font-display);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-secondary-lg:hover { border-color: rgba(255,255,255,0.2); background: var(--surface2); }

  .hero-stats { display: flex; justify-content: center; gap: 48px; flex-wrap: wrap; }
  .hero-stat { text-align: center; }
  .hero-stat-num { font-size: 32px; font-weight: 800; font-family: var(--font-mono); color: var(--accent); display: block; }
  .hero-stat-label { font-size: 12px; color: var(--muted); font-family: var(--font-mono); letter-spacing: 0.08em; text-transform: uppercase; }

  /* DIVIDER */
  .divider { height: 1px; background: var(--border); max-width: 860px; margin: 0 auto; }

  /* SECTIONS */
  .section { max-width: 860px; margin: 0 auto; padding: 80px 24px; }
  .section-label { font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.2em; color: var(--accent); text-transform: uppercase; text-align: center; margin-bottom: 16px; }
  .section-title { font-size: 32px; font-weight: 800; text-align: center; margin-bottom: 48px; color: var(--text); }

  .features-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; }

  .feature-card { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 28px; transition: border-color 0.2s; }
  .feature-card:hover { border-color: rgba(0,212,255,0.2); }
  .feature-icon { font-size: 28px; margin-bottom: 16px; display: block; }
  .feature-title { font-size: 16px; font-weight: 700; margin-bottom: 8px; color: var(--text); }
  .feature-desc { font-size: 14px; color: var(--muted); line-height: 1.6; }

  /* EXAMS */
  .exams-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 20px; }

  .exam-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    transition: all 0.2s;
    cursor: pointer;
    position: relative;
    overflow: hidden;
  }

  .exam-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, var(--accent), var(--accent2)); opacity: 0; transition: opacity 0.2s; }
  .exam-card:hover { border-color: rgba(0,212,255,0.25); transform: translateY(-2px); }
  .exam-card:hover::before { opacity: 1; }

  .exam-tag { font-family: var(--font-mono); font-size: 11px; color: var(--accent); background: rgba(0,212,255,0.08); border: 1px solid rgba(0,212,255,0.15); padding: 4px 10px; border-radius: 100px; display: inline-block; letter-spacing: 0.05em; }
  .exam-name { font-size: 18px; font-weight: 700; color: var(--text); line-height: 1.3; }
  .exam-meta { display: flex; align-items: center; justify-content: space-between; margin-top: auto; }
  .exam-questions { font-size: 13px; color: var(--muted); font-family: var(--font-mono); }
  .exam-price { font-size: 20px; font-weight: 800; color: var(--text); font-family: var(--font-mono); }
  .exam-price span { font-size: 13px; font-weight: 400; color: var(--muted); }

  .coming-soon { opacity: 0.5; cursor: default; }
  .coming-soon:hover { transform: none; border-color: var(--border); }
  .coming-soon::before { display: none; }
  .coming-soon-badge { font-family: var(--font-mono); font-size: 11px; color: var(--muted); background: var(--surface2); border: 1px solid var(--border); padding: 4px 10px; border-radius: 100px; display: inline-block; }

  /* PRICING */
  .pricing-card {
    background: var(--surface);
    border: 1px solid rgba(0,212,255,0.2);
    border-radius: 20px;
    padding: 48px;
    text-align: center;
    max-width: 480px;
    margin: 0 auto;
    position: relative;
    overflow: hidden;
  }

  .pricing-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, var(--accent), var(--accent2)); }
  .pricing-label { font-family: var(--font-mono); font-size: 11px; color: var(--accent); letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 20px; }
  .pricing-amount { font-size: 64px; font-weight: 800; font-family: var(--font-mono); color: var(--text); line-height: 1; margin-bottom: 8px; }
  .pricing-amount sup { font-size: 28px; vertical-align: super; }
  .pricing-period { font-size: 14px; color: var(--muted); margin-bottom: 32px; }

  .pricing-features { list-style: none; margin-bottom: 36px; text-align: left; display: flex; flex-direction: column; gap: 14px; }
  .pricing-features li { display: flex; align-items: center; gap: 12px; font-size: 15px; color: var(--text); }
  .pricing-features li::before { content: '✓'; color: var(--success); font-weight: 700; font-size: 16px; flex-shrink: 0; }

  /* FOOTER */
  .footer {
    border-top: 1px solid var(--border);
    padding: 32px 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }

  .footer-logo { font-size: 16px; font-weight: 700; color: var(--text); display: flex; align-items: center; gap: 8px; }
  .footer-links { display: flex; gap: 24px; align-items: center; }

  .footer-link {
    font-size: 13px;
    color: var(--muted);
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--font-display);
    transition: color 0.2s;
    text-decoration: none;
  }

  .footer-link:hover { color: var(--text); }
  .footer-copy { font-size: 12px; color: var(--muted); font-family: var(--font-mono); }

  /* MODAL */
  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; backdrop-filter: blur(2px); }
  .modal-box { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 32px; z-index: 201; width: 480px; max-width: 90vw; box-shadow: 0 24px 64px rgba(0,0,0,0.6); animation: fadeUp 0.2s ease; }
  .modal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
  .modal-title { font-size: 18px; font-weight: 700; }
  .modal-close { background: var(--surface2); color: var(--muted); border: 1px solid var(--border); width: 28px; height: 28px; border-radius: 6px; cursor: pointer; font-size: 13px; display: flex; align-items: center; justify-content: center; transition: all 0.15s; }
  .modal-close:hover { color: var(--text); }
  .modal-body { font-size: 14px; color: var(--muted); line-height: 1.7; }

  @keyframes fadeUp { from { opacity: 0; transform: translate(-50%, calc(-50% + 10px)); } to { opacity: 1; transform: translate(-50%, -50%); } }

  @media (max-width: 600px) {
    .nav { padding: 16px 20px; }
    .hero { padding: 60px 20px 60px; }
    .section { padding: 60px 20px; }
    .footer { padding: 24px 20px; flex-direction: column; align-items: flex-start; }
    .hero-stats { gap: 32px; }
    .pricing-card { padding: 32px 24px; }
  }
`;

export default function Landing() {
  const navigate = useNavigate();
  const [showRefund, setShowRefund] = useState(false);
  const [exams, setExams] = useState([]);

  useEffect(() => {
    // Fetch all exams
    supabase
      .from("exams")
      .select("id, name, slug, price, active")
      .order("id", { ascending: true })
      .then(({ data }) => {
        if (!data) return;
        setExams(data);
 });
  }, []);
       

  return (
    <>
      <style>{styles}</style>
      <div className="landing">

        {/* NAV */}
        <nav className="nav">
          <div className="nav-logo" onClick={() => navigate("/")}>
            <span>🐧</span>
            PassPenguin
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate("/login")}>Log in</button>
            <button className="btn-accent" onClick={() => navigate("/signup")}>Sign up free</button>
          </div>
        </nav>

        {/* HERO */}
        <section className="hero">
          <p className="hero-eyebrow">Salesforce Certification Practice</p>
          <h1 className="hero-title">
            Prepare for your<br />
            <span className="accent">Salesforce certification exam.</span>
          </h1>
          <p className="hero-sub">
            Practice exams built to mirror the real thing - not dumps.
            Detailed explanations on every question so you actually learn, not just memorise.
          </p>
          <p className="hero-eyebrow">
            Last Updated June 2026
          </p>
          <div className="hero-actions">
            <button className="btn-primary-lg" onClick={() => navigate("/signup")}>
              Get started →
            </button>
          </div>
          <div className="hero-stats">
            <div className="hero-stat">
              <span className="hero-stat-num">200</span>
              <span className="hero-stat-label">Questions per exam</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-num">$40</span>
              <span className="hero-stat-label">Per exam AUD</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-num">∞</span>
              <span className="hero-stat-label">Lifetime access</span>
            </div>
          </div>
        </section>

        <div className="divider" />

        {/* FEATURES */}
        <section className="section">
          <p className="section-label">Why PassPenguin</p>
          <h2 className="section-title">Built differently</h2>
          <div className="features-grid">
            <div className="feature-card">
              <span className="feature-icon">📝</span>
              <p className="feature-title">Not a dump</p>
              <p className="feature-desc">Every question is crafted to reflect the style and difficulty of the real exam - not copied from braindumps. Questions relating to depricated features are removed and with each release, new questions are crafted.</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">💡</span>
              <p className="feature-title">Learn why, not just what</p>
              <p className="feature-desc">Every question includes a detailed explanation so you understand the concept, not just the answer.</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">🎯</span>
              <p className="feature-title">Track your progress</p>
              <p className="feature-desc">See which questions you got right, wrong, and which topics need more work - all in one place.</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">♾️</span>
              <p className="feature-title">Lifetime access</p>
              <p className="feature-desc">Pay once, access forever. No subscriptions, no renewals. Yours to keep.</p>
            </div>
          </div>
        </section>

        <div className="divider" />

        {/* PRICING */}
        <section className="section">
          <p className="section-label">Pricing</p>
          <h2 className="section-title">Simple, no surprises</h2>
          <div className="pricing-card">
            <p className="pricing-label">Per exam</p>
            <p className="pricing-amount"><sup>$</sup>40</p>
            <p className="pricing-period">AUD · one-time payment · lifetime access</p>
            <ul className="pricing-features">
              <li>200 practice questions</li>
              <li>Detailed explanation on every question</li>
              <li>Topic-by-topic breakdown</li>
              <li>Unlimited attempts</li>
              <li>Question navigator</li>
              <li>Lifetime access - no subscription</li>
            </ul>
            <button className="btn-primary-lg" style={{ width: '100%' }} onClick={() => navigate("/signup")}>
              Get started →
            </button>
          </div>
        </section>

        
        <div className="divider" />

        {/* EXAMS */}
        <section className="section">
          <p className="section-label">Available now</p>
          <h2 className="section-title">Choose your exam</h2>
          <div className="exams-grid">
            {exams.map(exam =>
              exam.active ? (
                <div key={exam.id} className="exam-card" onClick={() => navigate("/signup")}>
                  <span className="exam-tag">AVAILABLE NOW</span>
                  <p className="exam-name">{exam.name}</p>
                </div>
              ) : (
                <div key={exam.id} className="exam-card coming-soon">
                  <span className="coming-soon-badge">COMING SOON</span>
                  <p className="exam-name">{exam.name}</p>
                </div>
              )
            )}
          </div>
        </section>
        <div className="divider" />

        {/* FOOTER */}
        <footer className="footer">
          <div className="footer-logo">🐧 PassPenguin</div>
          <div className="footer-links">
            <button className="footer-link" onClick={() => setShowRefund(true)}>Refund Policy</button>
            <a className="footer-link" href="/terms-of-service">Terms of Service</a>
            <a className="footer-link" href="/privacy-policy">Privacy Policy</a>
            <a className="footer-link" href="mailto:hello@passpenguin.com">Contact</a>
          </div>
          <p className="footer-copy">© {new Date().getFullYear()} PassPenguin</p>
        </footer>

      </div>

      {/* REFUND MODAL */}
      {showRefund && (
        <>
          <div className="modal-overlay" onClick={() => setShowRefund(false)} />
          <div className="modal-box">
            <div className="modal-header">
              <h2 className="modal-title">Refund Policy</h2>
              <button className="modal-close" onClick={() => setShowRefund(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p>All sales are final. As PassPenguin provides instant access to digital practice exam content, we do not offer refunds once a purchase has been made and access has been granted. If you are unsatisfied with your purchase, please contact us at hello@passpenguin.com to discuss further.</p>
              <br />
              <p>If you experience a technical issue preventing access to your purchased exam, please contact us at <a href="mailto:hello@passpenguin.com" style={{ color: 'var(--accent)' }}>hello@passpenguin.com</a> and we'll make it right.</p>
            </div>
          </div>
        </>
      )}
    </>
  );
}